import { ArrowLeftOutlined, RightCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Form, List, Modal, Result, Select } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { SetStateAction, useState } from 'react';
import { FeedBackTypeEnum } from '../../../__generated__/graphql';
import {
  FeedbackFormProps,
  FeedbackItem,
  ModalWithListProps,
  SuccessMessageProps,
} from '../../../types/feedbackModel.type';
import { GET_PROPERTY_TYPES } from '../../vastu-assessment/graphql/queries';
import { SHARE_FEEDBACK } from '../graphql/mutations';

//Import css
import '../styles/shareFeedbackModal.less';

const listData: FeedbackItem[] = [
  {
    key: '1',
    title: 'My score seems inaccurate.',
    feedbackType: FeedBackTypeEnum.ScoreSeemsInaccurate,
    subtitle:
      'We will help you as soon as you describe the problem in paragraphs below.',
  },
  {
    key: '2',
    title: 'Incorrect Object Selection',
    feedbackType: FeedBackTypeEnum.IncorrectObjectDetection,
    subtitle:
      'We will help you as soon as you select the correct property type from the dropdown below.',
  },
  {
    key: '3',
    title: 'Other',
    feedbackType: FeedBackTypeEnum.Other,
    subtitle:
      'We will help you as soon as you describe the problem in paragraphs below',
  },
];

const SuccessMessage = ({ onClose, onReset }: SuccessMessageProps) => (
  <Result
    className="feedback-success"
    status="success"
    title="Report Submitted"
    subTitle="Thank you for letting us know what’s going on. We will review your report and take appropriate action."
    extra={
      <Button
        className="feedback-modal-btn"
        type="primary"
        onClick={() => {
          onClose();
          onReset();
        }}
      >
        Got It
      </Button>
    }
  />
);

const FeedbackForm = ({
  floorPlanid,
  feedbackType,
  subtitle,
  isDropdown,
  onSuccess,
}: FeedbackFormProps) => {
  const [charCount, setCharCount] = useState<number>(0);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const maxLength = 255;
  const [form] = Form.useForm();
  const [submitFeedback, { loading }] = useMutation(SHARE_FEEDBACK);

  const { data: propertyTypesData } = useQuery(GET_PROPERTY_TYPES);
  const propertyOptions = propertyTypesData?.getAllPropertyTypesWeb?.data
    ?.filter((property) => property !== null && property?.propertyName)
    .map((property) => ({
      value: property?.propertyName,
      label: property?.propertyName,
    }));

  const handleSelectChange = (value: SetStateAction<string | null>) => {
    setSelectedValue(value);
  };

  const handleSubmit = async (values: { description: string }) => {
    await submitFeedback({
      variables: {
        data: {
          description: values?.description,
          feedbackType,
          floorPlanId: floorPlanid,
        },
      },
    });
    form.resetFields();
    setCharCount(0);
    onSuccess();
  };

  return (
    <>
      <div className="issue-descrpition">{subtitle}</div>
      <Form layout="vertical" form={form} onFinish={handleSubmit}>
        <Form.Item
          label="Let us know what's going on."
          name="description"
          rules={[
            { required: true, message: 'This field is required' },
            { max: maxLength, message: `Max ${maxLength} characters` },
            {
              validator: (_, value) => {
                if (value && value.trimStart() !== value) {
                  return Promise.reject(
                    new Error('Only spaces are not allowed!'),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          {isDropdown ? (
            <Select
              placeholder="Select Property Type"
              options={propertyOptions}
              onChange={handleSelectChange}
            />
          ) : (
            <TextArea
              rows={8}
              maxLength={maxLength}
              onChange={(e) => setCharCount(e.target.value.length)}
            />
          )}
        </Form.Item>
        {!isDropdown && (
          <div>
            {charCount}/{maxLength} characters
          </div>
        )}
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          className="feedback-modal-btn"
          disabled={
            (!isDropdown && charCount === 0) || (isDropdown && !selectedValue)
          }
        >
          Submit Report
        </Button>
      </Form>
    </>
  );
};

const FeedbackModal = ({
  modalOpen,
  onClose,
  floorPlanid,
}: ModalWithListProps) => {
  const [FeedbackType, setFeedbackType] = useState<FeedbackItem | null>(null);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const handleclose = () => {
    setFeedbackType(null);
    setIsSuccess(false);
    onClose();
  };

  return (
    <Modal
      className="share-feedback-modal"
      open={modalOpen}
      onCancel={handleclose}
      footer={null}
      title={
        FeedbackType ? (
          <div className="modal-title">
            {!isSuccess && (
              <ArrowLeftOutlined
                className="back-icon"
                onClick={() => setFeedbackType(null)}
              />
            )}
            {FeedbackType?.title}
          </div>
        ) : (
          'Report Issue'
        )
      }
    >
      {isSuccess ? (
        <SuccessMessage
          onClose={onClose}
          onReset={() => {
            setFeedbackType(null);
            setIsSuccess(false);
          }}
        />
      ) : FeedbackType ? (
        <FeedbackForm
          floorPlanid={floorPlanid}
          feedbackType={FeedbackType.feedbackType}
          subtitle={FeedbackType.subtitle}
          isDropdown={FeedbackType.key === '2'}
          onSuccess={() => setIsSuccess(true)}
        />
      ) : (
        <>
          <div className="issue-description">
            Did you find any issues with your Vastu score results?
          </div>
          <List
            dataSource={listData}
            renderItem={(item) => (
              <List.Item
                className="issue-list-item"
                onClick={() => setFeedbackType(item)}
              >
                <div>{item.title}</div>
                <RightCircleOutlined className="direction-arrow" />
              </List.Item>
            )}
          />
        </>
      )}
    </Modal>
  );
};

export default FeedbackModal;
